// ─── Impostazioni → 🌙 Caparra della fascia serale ───────────────────────────
//
// Dal mockup approvato il 25/09/2026 (sezione 6). Spenta, il bot prenota come
// prima; accesa, vale per le prenotazioni fatte da quel giorno in poi. Tutti i
// numeri del documento «Caparra Premium con SumUp» stanno qui, in un posto solo.
//
// 🔒 La chiave di SumUp NON si scrive qui: sta nel cluster. Qui si vede solo se
// c'è, se è quella di prova, e se il collegamento risponde.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  NON_PRIMA_DELLE_MAX,
  NON_PRIMA_DELLE_MIN,
  parseCents,
  type ImpostazioniCaparra,
  type StatoSumup,
  type StatoDelLettore,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, euro, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'caparra-impostazioni';
const GIORNI = [
  [1, 'Lun'],
  [2, 'Mar'],
  [3, 'Mer'],
  [4, 'Gio'],
  [5, 'Ven'],
  [6, 'Sab'],
  [0, 'Dom'],
] as const;

type Pannello = { settings: ImpostazioniCaparra; sumup: StatoSumup };

type Modulo = {
  attiva: boolean;
  dal: string;
  importo: string;
  ore: string;
  minuti: string;
  /** 🆕 Fase 2: i minuti per toccare «Paga la caparra» sul link della Segreteria. */
  tocco: string;
  vigilia: string;
  orePrima: string;
  /** 🆕 26/09/2026: «Mai prima delle», lo decide il circolo (prima il bot, alle 8). */
  nonPrimaDelle: string;
  noShowQuanti: string;
  noShowMesi: string;
  noShowCaparra: string;
  /** 🔁 27/09/2026: il campo fisso chiesto al bot. */
  fissoAttivo: boolean;
  fissoMesi: string;
  fascia: ImpostazioniCaparra['fascia'];
  url: string;
  versione: string;
};

const daSalvata = (s: ImpostazioniCaparra): Modulo => ({
  attiva: s.attiva,
  dal: s.dal ?? '',
  importo: (s.importoCents / 100).toFixed(2).replace('.', ','),
  ore: String(s.oreDisdetta),
  minuti: String(s.minutiPerPagare),
  tocco: String(s.minutiPerToccare),
  vigilia: s.avvisi.vigiliaAlle,
  orePrima: String(s.avvisi.orePrima),
  nonPrimaDelle: s.avvisi.nonPrimaDelle,
  noShowQuanti: String(s.noShow.quanti),
  noShowMesi: String(s.noShow.mesi),
  noShowCaparra: String(s.noShow.mesiCaparra),
  fissoAttivo: s.fissoDalBot.attivo,
  fissoMesi: String(s.fissoDalBot.mesiMax),
  fascia: s.fascia,
  url: s.regolamento.url ?? '',
  versione: s.regolamento.versione,
});

const intero = (s: string, min: number, max: number): number | undefined => {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

const ORARIO = /^([01]\d|2[0-3]):[0-5]\d$/;

/** «Alla seconda scatta la regola»: le partite saltate si contano al femminile. */
const ORDINALI = [
  'prima',
  'seconda',
  'terza',
  'quarta',
  'quinta',
  'sesta',
  'settima',
  'ottava',
  'nona',
  'decima',
];

const dataDetta = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Caparra(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({ queryKey: [CHIAVE], queryFn: () => chiama<Pannello>('GET', '/settings/deposit') });
  if (letta.isLoading) return <Attesa cosa="le impostazioni dell’acconto" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <Pagina pannello={letta.data} modificabile={modificabile} />;
}

function Pagina({
  pannello,
  modificabile,
}: {
  pannello: Pannello;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvata(pannello.settings));
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setM(daSalvata(pannello.settings)), [pannello.settings]);
  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };

  const importo = parseCents(m.importo);
  const ore = intero(m.ore, 1, 336);
  const minuti = intero(m.minuti, 5, 120);
  const tocco = intero(m.tocco, 10, 1440);
  const orePrima = intero(m.orePrima, 1, 12);
  const vigiliaOk = ORARIO.test(m.vigilia.trim());
  const alba = m.nonPrimaDelle.trim();
  const albaOk = ORARIO.test(alba) && alba >= NON_PRIMA_DELLE_MIN && alba <= NON_PRIMA_DELLE_MAX;
  const nsQuanti = intero(m.noShowQuanti, 1, 10);
  const nsMesi = intero(m.noShowMesi, 1, 36);
  const nsCaparra = intero(m.noShowCaparra, 1, 24);
  const fissoMesi = intero(m.fissoMesi, 1, 6);
  const fasciaStorta = m.fascia.some((f) => f.giorni.length === 0 || f.dalle > f.alle);
  const pronta =
    importo !== null &&
    importo >= 100 &&
    ore !== undefined &&
    minuti !== undefined &&
    tocco !== undefined &&
    orePrima !== undefined &&
    vigiliaOk &&
    albaOk &&
    nsQuanti !== undefined &&
    nsMesi !== undefined &&
    nsCaparra !== undefined &&
    fissoMesi !== undefined &&
    !fasciaStorta;

  const salva = useMutation({
    meta: { fatto: 'impostazioni dell’acconto salvate' },
    mutationFn: () =>
      chiama<ImpostazioniCaparra>('PUT', '/settings/deposit', {
        attiva: m.attiva,
        dal: m.dal || null,
        importoCents: importo,
        oreDisdetta: ore,
        minutiPerPagare: minuti,
        minutiPerToccare: tocco,
        avvisi: { vigiliaAlle: m.vigilia.trim(), orePrima, nonPrimaDelle: alba },
        noShow: { quanti: nsQuanti, mesi: nsMesi, mesiCaparra: nsCaparra },
        fissoDalBot: { attivo: m.fissoAttivo, mesiMax: fissoMesi },
        fascia: m.fascia,
        // 📄 Voce 37: il regolamento lo scrive «Documenti del bot»; il server tiene il suo.
        regolamento: { url: m.url || null, versione: m.versione },
      }),
    onSuccess: () => {
      setSalvato(true);
      rileggi();
    },
  });
  const prova = useMutation({
    meta: { fatto: 'collegamento con SumUp provato: il risultato è qui sopra' },
    mutationFn: () => chiama('POST', '/settings/deposit/check'),
    onSettled: () => rileggi(),
  });

  const riga = (
    i: number,
    f: (r: ImpostazioniCaparra['fascia'][number]) => ImpostazioniCaparra['fascia'][number],
  ) => cambia((x) => ({ ...x, fascia: x.fascia.map((r, j) => (j === i ? f(r) : r)) }));

  const k = pannello.sumup;

  return (
    <section>
      <header className="testata">
        <h3>Acconto della fascia serale</h3>
        <p className="tenue">
          Di sera, da lunedì a venerdì, per prenotare serve un acconto che poi si scala dal totale. Disdetta
          in tempo, o partita annullata dal circolo: torna nel borsellino come Crediti. Campo vuoto, o
          disdetta a meno delle ore qui sotto: resta al circolo (si può restituire con «💶 Accredita a
          discrezione»). Ai soci il bot dice «fascia serale».
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronta && modificabile) salva.mutate();
        }}
      >
        <fieldset className="riquadro" disabled={!modificabile}>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={m.attiva}
              onChange={(e) => cambia((x) => ({ ...x, attiva: e.target.checked }))}
            />{' '}
            <strong>{m.attiva ? 'Accesa' : 'Spenta'}</strong>
          </label>
          <p className="tenue piccolo">
            Spenta, il bot prenota come oggi. Accesa, vale per le prenotazioni fatte da quel momento in poi:
            quelle che esistono già non chiedono niente.
          </p>
          <label className="campo">
            <span>Vale dalle prenotazioni fatte dal (vuoto: da subito)</span>
            <input
              type="date"
              value={m.dal}
              onChange={(e) => cambia((x) => ({ ...x, dal: e.target.value }))}
            />
          </label>
          <Campo
            etichetta="Importo (€)"
            inputMode="decimal"
            value={m.importo}
            onChange={(e) => cambia((x) => ({ ...x, importo: e.target.value }))}
          />
          {(importo === null || importo < 100) && (
            <p className="guasto">Scrivi un importo, per esempio 30,00.</p>
          )}
          <Campo
            etichetta="L’acconto nuovo del campo fisso, entro quante ore prima"
            inputMode="numeric"
            value={m.ore}
            onChange={(e) => cambia((x) => ({ ...x, ore: e.target.value }))}
          />
          {ore === undefined && <p className="guasto">Scrivi un numero di ore, per esempio 48.</p>}
          <Campo
            etichetta="Minuti per pagare (il campo resta tenuto)"
            inputMode="numeric"
            value={m.minuti}
            onChange={(e) => cambia((x) => ({ ...x, minuti: e.target.value }))}
          />
          {minuti === undefined && <p className="guasto">Fra 5 e 120 minuti.</p>}
          <Campo
            etichetta="Link della Segreteria: minuti per toccare «Paga l’acconto»"
            inputMode="numeric"
            value={m.tocco}
            onChange={(e) => cambia((x) => ({ ...x, tocco: e.target.value }))}
          />
          <p className="tenue piccolo">
            Contano da quando il messaggio arriva al socio; a metà parte un promemoria. Quando lo tocca, ha i
            minuti per pagare qui sopra.
          </p>
          {tocco === undefined && <p className="guasto">Fra 10 e 1440 minuti.</p>}
          {/* 🔄 Voce 36 (27/09/2026): quanto valgono i Crediti sta in una regola sola, con quelli del premio. */}
          <p className="tenue piccolo">
            🔄 Quanto valgono i Crediti che tornano da un acconto, e quando si avvisa il socio: nella
            linguetta <Link to="/impostazioni/borsellino/crediti">Crediti</Link>.
          </p>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Avvisi sulla partita, all'organizzatore</h3>
          <p className="tenue piccolo">
            Per tutte le partite, di giorno e di sera. Arrivano solo a chi ha organizzato.
          </p>
          <Campo
            etichetta="Il giorno prima, alle"
            value={m.vigilia}
            onChange={(e) => cambia((x) => ({ ...x, vigilia: e.target.value }))}
          />
          <p className="tenue piccolo">
            Solo se la partita non è ancora completa: le idee per trovare chi manca.
          </p>
          {!vigiliaOk && <p className="guasto">Scrivi un'ora, per esempio 18:00.</p>}
          <Campo
            etichetta="Il giorno della partita, quante ore prima"
            inputMode="numeric"
            value={m.orePrima}
            onChange={(e) => cambia((x) => ({ ...x, orePrima: e.target.value }))}
          />
          <p className="tenue piccolo">
            Se sono in quattro, la conferma; se manca qualcuno, le idee per completarla.
          </p>
          {orePrima === undefined && <p className="guasto">Fra 1 e 12 ore.</p>}
          {/*
            🆕 26/09/2026 — 🗣️ *«Voglio deciderlo io quando è fisso, ma comunque se
            dobbiamo decidere un fisso sono le 7 di mattina»*. Vale per tutti gli
            avvisi all'organizzatore; le proposte delle 8 e delle 13 restano dove sono.
          */}
          <Campo
            etichetta="Mai prima delle"
            value={m.nonPrimaDelle}
            onChange={(e) => cambia((x) => ({ ...x, nonPrimaDelle: e.target.value }))}
          />
          <p className="tenue piccolo">
            Lo decidi tu; se non lo tocchi vale 07:00. Se il conto cade prima di quest’ora, l’avviso arriva a
            quest’ora.
          </p>
          {!albaOk && (
            <p className="guasto">
              Scrivi un'ora fra le {NON_PRIMA_DELLE_MIN} e le {NON_PRIMA_DELLE_MAX}, per esempio 07:00.
            </p>
          )}
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>No-show ripetuti</h3>
          <p className="tenue piccolo">
            Un no-show è una partita in cui il campo è rimasto vuoto, segnata dalla Segreteria con «🚫 Segna
            no-show». Contano <strong>tutte</strong>, con l’acconto o senza: con l’acconto, l’acconto resta al
            circolo; senza, al socio non costa niente, ma l’assenza si conta lo stesso.
          </p>
          <Campo
            etichetta="Quante partite saltate"
            inputMode="numeric"
            value={m.noShowQuanti}
            onChange={(e) => cambia((x) => ({ ...x, noShowQuanti: e.target.value }))}
          />
          {nsQuanti !== undefined && (
            <p className="tenue piccolo">
              {nsQuanti === 1
                ? 'Alla prima scatta la regola.'
                : `Alla ${ORDINALI[nsQuanti - 1] ?? `${nsQuanti}ª`} scatta la regola.`}
            </p>
          )}
          {/* 🔄 26/09/2026 — era «In quanti mesi»: 🗣️ *«non ho capito cosa significa»*. */}
          <Campo
            etichetta="Una partita saltata si dimentica dopo … mesi"
            inputMode="numeric"
            value={m.noShowMesi}
            onChange={(e) => cambia((x) => ({ ...x, noShowMesi: e.target.value }))}
          />
          {nsMesi !== undefined && nsQuanti !== undefined && (
            <p className="tenue piccolo">
              È la memoria del gestionale: dopo {nsMesi} {nsMesi === 1 ? 'mese' : 'mesi'} una partita saltata
              si cancella dal conto.
              {nsQuanti > 1 &&
                ` Quindi la regola scatta solo se le ${nsQuanti} partite saltate stanno dentro ${nsMesi} ${nsMesi === 1 ? 'mese' : 'mesi'}.`}
            </p>
          )}
          <Campo
            etichetta="Poi l’acconto serve per … mesi"
            inputMode="numeric"
            value={m.noShowCaparra}
            onChange={(e) => cambia((x) => ({ ...x, noShowCaparra: e.target.value }))}
          />
          {nsCaparra !== undefined && (
            <p className="tenue piccolo">
              Dal giorno della partita saltata che fa scattare la regola, per {nsCaparra}{' '}
              {nsCaparra === 1 ? 'mese' : 'mesi'} ogni sua prenotazione chiede l’acconto, anche di giorno. Poi
              si torna come prima, da sola.
            </p>
          )}
          {(nsQuanti === undefined || nsMesi === undefined || nsCaparra === undefined) && (
            <p className="guasto">Scrivi tre numeri, per esempio 2, 12 e 3.</p>
          )}
        </fieldset>

        {/* 🔁 27/09/2026 — mockup «Fisso dal bot», approvato: due mesi, e nasce subito. */}
        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>🔁 Campi fissi dal bot</h3>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={m.fissoAttivo}
              onChange={(e) => cambia((x) => ({ ...x, fissoAttivo: e.target.checked }))}
            />{' '}
            I soci possono prenotare un campo fisso dall’assistente
          </label>
          <Campo
            etichetta="Durata massima, in mesi"
            inputMode="numeric"
            value={m.fissoMesi}
            onChange={(e) => cambia((x) => ({ ...x, fissoMesi: e.target.value }))}
          />
          {fissoMesi === undefined && <p className="guasto">Scrivi un numero da 1 a 6, per esempio 2.</p>}
          <p className="tenue piccolo">
            Si ripete ogni settimana, stesso giorno, stessa ora, stesso campo: gli altri ritmi e le durate più
            lunghe li fa la Segreteria. Il campo fisso nasce subito, come una prenotazione, e nel calendario
            ha il segno 🔁 «dal bot». Di sera chiede un acconto solo per tutto il campo fisso.
          </p>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Fascia serale</h3>
          <p className="tenue piccolo">
            Conta l’ora d’<strong>inizio</strong> della partita, non un elenco di turni: una partita che
            inizia alle 18:30 è in fascia serale anche se quel turno oggi non c’è.
          </p>
          {m.fascia.map((f, i) => (
            <div key={i} className="riga-fascia-caparra">
              <div className="giorni-caparra">
                {GIORNI.map(([n, nome]) => (
                  <label key={n}>
                    <input
                      type="checkbox"
                      checked={f.giorni.includes(n)}
                      onChange={(e) =>
                        riga(i, (r) => ({
                          ...r,
                          giorni: e.target.checked
                            ? [...r.giorni, n].sort()
                            : r.giorni.filter((g) => g !== n),
                        }))
                      }
                    />
                    {nome}
                  </label>
                ))}
              </div>
              <label className="campo">
                <span>dalle</span>
                <input
                  type="time"
                  value={f.dalle}
                  onChange={(e) => riga(i, (r) => ({ ...r, dalle: e.target.value }))}
                />
              </label>
              <label className="campo">
                <span>alle</span>
                <input
                  type="time"
                  value={f.alle}
                  onChange={(e) => riga(i, (r) => ({ ...r, alle: e.target.value }))}
                />
              </label>
              <button
                type="button"
                className="bordato"
                onClick={() => cambia((x) => ({ ...x, fascia: x.fascia.filter((_, j) => j !== i) }))}
              >
                Togli
              </button>
            </div>
          ))}
          {fasciaStorta && (
            <p className="guasto">Ogni riga vuole almeno un giorno, e «dalle» prima di «alle».</p>
          )}
          <button
            type="button"
            className="bordato"
            onClick={() =>
              cambia((x) => ({
                ...x,
                fascia: [...x.fascia, { giorni: [1, 2, 3, 4, 5], dalle: '18:00', alle: '21:00' }],
              }))
            }
          >
            ➕ Aggiungi una riga
          </button>
        </fieldset>

        {/*
          📄 Voce 37 (27/09/2026, mockup approvato «37 AAA»). 🗣️ *«mi scrivi di
          fianco alla voce regolamento per quale attività si deve inserire»*.
          🔄 Il testo non si scrive più qui: lo si scrive e pubblica in
          «Documenti del bot», e indirizzo e versione arrivano da lì. La riga
          vecchia diceva «al primo pagamento», che non era esatto: il bot lo
          chiede PRIMA di prenotare, cioè prima di pagare l'acconto.
        */}
        <div className="riquadro regolamento-acconto">
          <h3>
            Regolamento <span className="per-cosa">· per l’acconto delle partite della sera</span>
          </h3>
          <dl className="coppie">
            <dt>Il testo</dt>
            <dd>
              scritto in{' '}
              <Link to="/impostazioni/documenti?tipo=regolamento">
                Documenti del bot → Regolamento dell’acconto
              </Link>
            </dd>
            <dt>Indirizzo</dt>
            <dd className="indirizzo">{m.url || <span className="tenue">non ancora pubblicato</span>}</dd>
            <dt>Versione</dt>
            <dd>{m.versione}</dd>
          </dl>
          <p className="tenue piccolo">
            Il bot lo fa accettare <strong>prima di prenotare una partita della sera</strong> (anche col campo
            fisso) e <strong>quando l’organizzazione di una partita della sera passa a un altro socio</strong>
            : si segna giorno, ora e versione. Con una versione nuova lo richiede a tutti, alla loro prossima
            partita della sera.
          </p>
        </div>

        <div className="riquadro">
          <h3>SumUp</h3>
          <dl className="coppie">
            <dt>Collegamento</dt>
            <dd>
              {k.configured ? (
                <>
                  <span className={`pill-caparra ${k.sandbox ? 'pill-attesa' : 'pill-ok'}`}>
                    {k.sandbox ? 'Di prova (sandbox)' : 'Vero'}
                  </span>{' '}
                  · codice commerciante {k.merchantCodeHint}
                </>
              ) : (
                'Non configurato: la chiave va messa nel cluster. Senza, l’acconto si paga solo coi Crediti o al banco.'
              )}
            </dd>
            <dt>Notifica dei pagamenti</dt>
            <dd>{k.webhook ? 'Attiva' : 'Spenta: il gestionale rilegge da solo ogni minuto.'}</dd>
            <dt>Ultimo controllo</dt>
            <dd>
              {k.lastCheck
                ? `${dataDetta(k.lastCheck.at)}, ${k.lastCheck.ok ? 'tutto a posto' : `non risponde (${k.lastCheck.detail ?? ''})`}`
                : 'mai'}
            </dd>
          </dl>
          <p className="tenue piccolo">
            La chiave non si scrive qui: sta nel cluster. Qui si vede solo se c’è.
          </p>
          {modificabile && (
            <button
              type="button"
              className="bordato"
              disabled={prova.isPending}
              onClick={() => prova.mutate()}
            >
              {prova.isPending ? 'Un attimo…' : 'Prova il collegamento'}
            </button>
          )}
        </div>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && (
              <p className="tenue">
                Salvato.{' '}
                {m.attiva
                  ? `Da adesso, di sera, il bot chiede ${importo !== null ? euro(importo) : ''}.`
                  : 'L’acconto è spento.'}
              </p>
            )}
            <button type="submit" disabled={salva.isPending || !pronta}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
      <LettoreSolo stato={k.reader} modificabile={modificabile} rileggi={rileggi} />
    </section>
  );
}

/**
 * 📟 🆕 26/09/2026 (fase 3, R9) — **il lettore SumUp Solo al banco.** Si
 * collega una volta sola: il Solo mostra un codice, lo si scrive qui. Da lì la
 * scheda della prenotazione manda l'importo al lettore, e il pagamento si segna
 * da solo.
 */
function LettoreSolo({
  stato,
  modificabile,
  rileggi,
}: {
  stato: StatoDelLettore;
  modificabile: boolean;
  rileggi(): void;
}): React.JSX.Element {
  const [codice, setCodice] = useState('');
  const [nome, setNome] = useState('Banco');
  const collega = useMutation({
    meta: { fatto: (d) => `lettore collegato: ${(d as StatoDelLettore).reader?.name ?? ''}` },
    mutationFn: () =>
      chiama<StatoDelLettore>('POST', '/settings/deposit/reader', {
        pairingCode: codice.trim(),
        name: nome.trim(),
      }),
    onSuccess: () => {
      setCodice('');
      rileggi();
    },
  });
  const scollega = useMutation({
    meta: { fatto: 'lettore scollegato' },
    mutationFn: () => chiama<StatoDelLettore>('DELETE', '/settings/deposit/reader'),
    onSuccess: rileggi,
  });
  const l = stato.reader;
  return (
    <div className="riquadro">
      <h3>📟 Lettore SumUp Solo, al banco</h3>
      {l ? (
        <dl className="coppie">
          <dt>Collegato</dt>
          <dd>
            <span className="pill-caparra pill-ok">{l.name}</span>
            {l.serial && <span className="tenue piccolo"> · numero {l.serial}</span>}
            <span className="tenue piccolo"> · dal {dataDetta(l.pairedAt)}</span>
          </dd>
          <dt>Si usa da qui</dt>
          <dd>
            {stato.ready ? 'Sì: la scheda della prenotazione ha «📟 Manda al lettore».' : stato.missing}
          </dd>
        </dl>
      ) : (
        <p className="piccolo">
          Sul Solo: <strong>Impostazioni → Collegamento → Collega con API</strong>. Il lettore mostra un
          codice di 8 o 9 lettere e cifre: scrivilo qui sotto.
          {stato.missing && !stato.missing.startsWith('Il lettore') && (
            <>
              {' '}
              <strong>⚠️ {stato.missing}</strong>
            </>
          )}
        </p>
      )}
      {modificabile && !l && (
        <div className="riga-campi">
          <label className="campo">
            <span>Codice sul lettore</span>
            <input value={codice} onChange={(e) => setCodice(e.target.value)} placeholder="ABCD1234" />
          </label>
          <label className="campo">
            <span>Nome</span>
            <input value={nome} onChange={(e) => setNome(e.target.value)} />
          </label>
          <button
            type="button"
            disabled={collega.isPending || !/^[A-Za-z0-9]{8,9}$/.test(codice.trim()) || nome.trim() === ''}
            onClick={() => collega.mutate()}
          >
            {collega.isPending ? 'Un attimo…' : 'Collega il lettore'}
          </button>
        </div>
      )}
      {modificabile && l && (
        <button
          type="button"
          className="bordato"
          disabled={scollega.isPending}
          onClick={() => scollega.mutate()}
        >
          Scollega il lettore
        </button>
      )}
      <Guasto errore={collega.error ?? scollega.error} />
    </div>
  );
}
